/**
 * Shared business totals from GetVatAdvancesPageData income/expense/equipment objects.
 *
 * Same derivation as VatAdvancesSection / Redux clientDataSlice via buildReduxPayload:
 * - incomesAmount ← income.totalWithoutVat
 * - expensesAmount ← expense.totalWithoutVat
 * - purchaseOfEquipmentExpensesAmount ← equipment.totalWithoutVat
 * - profitAmount (VAT card "רווח") ← incomes − expenses − equipment
 *
 * Raw VAT totals: profitBeforeTaxForCalculator = max(incomes − expenses, 0) (equipment not subtracted).
 * useAdvanceCalculator merges manual primary amounts and sets profitBeforeTaxForCalculator to
 * max(primaryNet + additionalExpectedIncome − additionalExpectedExpense, 0) for tax/NI.
 */

/**
 * @param {object} [income]
 * @param {object} [expense]
 * @param {object} [equipment]
 * @returns {{
 *   incomesWithoutVat: number,
 *   expensesWithoutVat: number,
 *   equipmentWithoutVat: number,
 *   profitAmountVatParity: number,
 *   profitBeforeTaxForCalculator: number,
 * }}
 */
export function deriveBusinessTotalsFromVatDocuments(income = {}, expense = {}, equipment = {}) {
  const incomesWithoutVat = Number(income.totalWithoutVat) || 0;
  const expensesWithoutVat = Number(expense.totalWithoutVat) || 0;
  const equipmentWithoutVat = Number(equipment.totalWithoutVat) || 0;
  const profitAmountVatParity = incomesWithoutVat - expensesWithoutVat - equipmentWithoutVat;
  const profitBeforeTaxForCalculator = Math.max(
    incomesWithoutVat - expensesWithoutVat,
    0
  );
  return {
    incomesWithoutVat,
    expensesWithoutVat,
    equipmentWithoutVat,
    profitAmountVatParity,
    profitBeforeTaxForCalculator,
  };
}

/**
 * @param {object} data - Raw JSON from GetVatAdvancesPageData
 * @returns {ReturnType<typeof deriveBusinessTotalsFromVatDocuments>}
 */
export function selectTotalsFromVatAdvancesApiData(data = {}) {
  return deriveBusinessTotalsFromVatDocuments(data.income, data.expense, data.equipment);
}

/**
 * Annualized income: (period profit ÷ months in period) × 12.
 * NI advance bracket base can use period profit ÷ selected inclusive months (M) for range-based monthly estimates.
 */
export function annualizedProfitByAssessment(profitForPeriod, inclusivePeriodMonths) {
  const m = Math.max(1, Number(inclusivePeriodMonths) || 1);
  const p = Number(profitForPeriod);
  const safe = Number.isFinite(p) ? p : 0;
  return (safe / m) * 12;
}

/** Inclusive count of calendar months spanned by [startDate, endDate] in UTC (min 1). */
export function inclusiveUtcMonthSpan(startDate, endDate) {
  if (!startDate || !endDate) return 1;
  const y1 = startDate.getUTCFullYear();
  const m1 = startDate.getUTCMonth();
  const y2 = endDate.getUTCFullYear();
  const m2 = endDate.getUTCMonth();
  return Math.max(1, (y2 - y1) * 12 + (m2 - m1) + 1);
}
